import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';

interface BulkAction {
  label: string;
  tooltip?: string;
  icon: ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'danger';
  dataBuilder?: string;
}

interface BulkActionBarProps {
  count: number;
  actions: BulkAction[];
  onClear: () => void;
}

export function BulkActionBar({ count, actions, onClear }: BulkActionBarProps) {
  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="fixed bottom-[5.5rem] lg:bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-[calc(100vw-2rem)]"
        >
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 bg-background-inverse text-text-inverse rounded-xl shadow-lg border border-white/10">
            <div className="flex items-center gap-2 text-sm font-medium whitespace-nowrap">
              <Check size={16} />
              <span>{count} selected</span>
            </div>

            <div className="w-px h-5 bg-white/20" />

            <div className="flex items-center gap-0.5 sm:gap-1.5">
              {actions.map((action) => (
                <button
                  key={action.label}
                  onClick={action.onClick}
                  title={action.tooltip || action.label}
                  data-builder={action.dataBuilder}
                  className={`
                    flex items-center justify-center gap-1.5 p-2 sm:px-3 sm:py-1.5 rounded-lg text-sm font-medium
                    transition-colors whitespace-nowrap
                    ${action.variant === 'danger'
                      ? 'hover:bg-error/20 text-red-300'
                      : 'hover:bg-white/10'
                    }
                  `}
                >
                  {action.icon}
                  <span className="hidden sm:inline">{action.label}</span>
                </button>
              ))}
            </div>

            <div className="w-px h-5 bg-white/20" />

            <button
              onClick={onClear}
              className="p-1 rounded-lg hover:bg-white/10 transition-colors"
              title="Deselect all"
            >
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
